import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { USER_REGISTRATION_LOGIN_TYPE } from 'ish-core/configurations/injection-keys';
import { SpecialHttpErrorHandler } from 'ish-core/interceptors/icm-error-mapper.interceptor';
import { HttpError } from 'ish-core/models/http-error/http-error.model';
import { InjectSingle } from 'ish-core/utils/injection';

/* eslint-disable @typescript-eslint/no-restricted-types */

@Injectable()
export class LoginUserErrorHandler implements SpecialHttpErrorHandler {
  constructor(
    @Inject(USER_REGISTRATION_LOGIN_TYPE) public loginType: InjectSingle<typeof USER_REGISTRATION_LOGIN_TYPE>
  ) {}

  test(error: HttpErrorResponse): boolean {
    return (error.status === 401 || error.status === 403) && error.url.includes('token');
  }
  map(error: HttpErrorResponse): Partial<HttpError> {
    // CUSTOMIZATION: FR 1.4.4 — map ICM lockout responses before generic invalid-credentials
    if (this.isAccountLockout(error)) {
      return { code: 'account.login.lockout.error' };
    }
    // CUSTOMIZATION: discovery — pending / disabled accounts before generic 403 approval mapping
    const pendingOrDisabled = this.mapPendingOrDisabled(error);
    if (pendingOrDisabled) {
      return { code: pendingOrDisabled };
    }
    if (error.status === 403) {
      return { code: 'account.login.customer_approval.error.invalid' };
    }
    if (this.loginType === 'email') {
      return { code: 'account.login.email_password.error.invalid' };
    } else {
      return { code: 'account.login.username_password.error.invalid' };
    }
  }

  private mapPendingOrDisabled(error: HttpErrorResponse): string | undefined {
    const body = error.error;
    const code = typeof body === 'object' && body ? String(body.error || body.code || '') : '';
    const messageKey =
      typeof body === 'object' && body ? String(body.messageKey || '') : '';
    if (code === 'USER_PENDING_APPROVAL' || messageKey === 'account.login.pending_approval.error') {
      return 'account.login.pending_approval.error';
    }
    if (code === 'USER_DISABLED' || messageKey === 'account.login.disabled_user.error') {
      return 'account.login.disabled_user.error';
    }
    return undefined;
  }

  /** Detects provisional ICM lockout payloads (confirm codes with backend). */
  private isAccountLockout(error: HttpErrorResponse): boolean {
    const headerKey = (error.headers?.get('error-key') || '').toLowerCase();
    if (headerKey.includes('lock') || headerKey.includes('locked')) {
      return true;
    }
    const body = error.error;
    const text =
      typeof body === 'string'
        ? body
        : JSON.stringify(body?.errors || body?.code || body?.message || body || '');
    const normalized = text.toLowerCase();
    return (
      normalized.includes('account_locked') ||
      normalized.includes('account locked') ||
      normalized.includes('lockout') ||
      normalized.includes('"locked"')
    );
  }
}
